import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { AuthContext } from '../../context/AuthContext';
import { useDialog } from '../../context/DialogContext';

// Lets the archiver freely add/remove members and pick the team leader on
// an already-created assignment - reachable both before the team has
// accepted (a plain edit) and after (status ACCEPTED_BY_EXAMINER, the job
// already live and possibly mid-scoring), the backend enforces the same
// status gate either way. Modeled on AssignmentModal's dropdown+Add
// examiner picker, but pre-seeded with the assignment's current team and
// able to remove members too.
function EditTeamModal({ show, onHide, assignment, onSuccess }) {
  const { user } = useContext(AuthContext);
  const accessToken = user?.token || localStorage.getItem('token');
  const { confirm } = useDialog();
  const isLiveJob = assignment?.status === 'ACCEPTED_BY_EXAMINER';

  const [availableExaminers, setAvailableExaminers] = useState([]);
  const [selected, setSelected] = useState([]); // [{id, username/fullName}]
  const [teamLeaderId, setTeamLeaderId] = useState('');
  const [examinerToAdd, setExaminerToAdd] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    if (!show || !assignment) return;
    setFeedback(null);
    setExaminerToAdd('');

    const currentExaminers = (assignment.examiners || []).map((e) => ({
      id: e.id,
      name: e.fullName || e.username
    }));
    setSelected(currentExaminers);
    setTeamLeaderId(assignment.teamLeader ? String(assignment.teamLeader.id) : (currentExaminers[0]?.id ? String(currentExaminers[0].id) : ''));

    if (!assignment.examDate || !assignment.examStartTime || !assignment.examDurationMinutes) {
      setFeedback({ type: 'error', message: 'Assignment missing date/time/duration - cannot look up available examiners.' });
      return;
    }
    setLoading(true);
    axios
      .get('/api/exam-assignments/available-examiners', {
        params: {
          date: assignment.examDate,
          startTime: assignment.examStartTime,
          durationMinutes: assignment.examDurationMinutes
        },
        headers: { Authorization: `Bearer ${accessToken}` }
      })
      .then((response) => {
        // Current team members are already booked into this very slot, so
        // the availability endpoint (which has no "except this assignment"
        // carve-out) will omit them - union them back in so they stay
        // selectable/removable regardless.
        const fetched = response.data.map((e) => ({ id: e.id, name: e.fullName || e.username }));
        const byId = new Map(fetched.map((e) => [e.id, e]));
        currentExaminers.forEach((e) => {
          if (!byId.has(e.id)) byId.set(e.id, e);
        });
        setAvailableExaminers(Array.from(byId.values()));
      })
      .catch(() => {
        setFeedback({ type: 'error', message: 'Error fetching available examiners.' });
      })
      .finally(() => setLoading(false));
  }, [show, assignment, accessToken]);

  const handleAddExaminer = () => {
    if (!examinerToAdd) return;
    const id = parseInt(examinerToAdd, 10);
    if (!selected.some((e) => e.id === id)) {
      const found = availableExaminers.find((e) => e.id === id);
      const next = [...selected, { id, name: found ? found.name : examinerToAdd }];
      setSelected(next);
      if (next.length === 1) setTeamLeaderId(String(id));
    }
    setExaminerToAdd('');
  };

  const handleRemove = (id) => {
    const next = selected.filter((e) => e.id !== id);
    setSelected(next);
    if (String(teamLeaderId) === String(id)) {
      setTeamLeaderId(next[0] ? String(next[0].id) : '');
    }
  };

  const handleSubmit = async () => {
    if (selected.length === 0) {
      setFeedback({ type: 'error', message: 'The team must have at least one examiner.' });
      return;
    }
    if (selected.length > 1 && !teamLeaderId) {
      setFeedback({ type: 'error', message: 'Please choose a team leader.' });
      return;
    }

    const removedMembers = (assignment.examiners || []).filter((e) => !selected.some((s) => s.id === e.id));
    const originalLeaderId = assignment.teamLeader ? assignment.teamLeader.id : null;
    const leaderIsChanging = selected.length > 1 && Number(teamLeaderId) !== originalLeaderId;

    if (isLiveJob && (removedMembers.length > 0 || leaderIsChanging)) {
      const parts = [];
      if (removedMembers.length > 0) {
        parts.push(
          `removing ${removedMembers.map((e) => e.fullName || e.username).join(', ')} - any candidate(s) they've `
          + `started but not yet completed will be released back to the pool for a teammate to finish; anything `
          + `they already completed keeps their name on it`
        );
      }
      if (leaderIsChanging) {
        const newLeaderName = selected.find((e) => String(e.id) === String(teamLeaderId))?.name;
        parts.push(`making ${newLeaderName || 'the selected examiner'} the new team leader going forward`);
      }
      const proceed = await confirm(
        `This job has already been accepted and may be mid-scoring. This change involves ${parts.join(' and ')}. Continue?`,
        { title: 'Edit a Live Team', confirmText: 'Save Changes', variant: 'danger' }
      );
      if (!proceed) return;
    }

    setSubmitting(true);
    setFeedback(null);
    axios
      .put(
        `/api/exam-assignments/${assignment.id}/team`,
        {
          examinerIds: selected.map((e) => e.id),
          teamLeaderId: selected.length > 1 ? parseInt(teamLeaderId, 10) : null
        },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      )
      .then(() => {
        setFeedback({ type: 'success', message: 'Team updated.' });
        if (onSuccess) onSuccess();
        setTimeout(() => onHide(), 1000);
      })
      .catch((err) => {
        const backendMessage = typeof err.response?.data === 'string' ? err.response.data : err.response?.data?.error;
        setFeedback({ type: 'error', message: backendMessage || 'Error updating team.' });
      })
      .finally(() => setSubmitting(false));
  };

  const selectableExaminers = availableExaminers.filter((e) => !selected.some((s) => s.id === e.id));

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Edit Team{assignment ? ` - ${assignment.moduleName || ''}` : ''}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {feedback && (
          <Alert variant={feedback.type === 'success' ? 'success' : 'danger'}>{feedback.message}</Alert>
        )}
        {isLiveJob && !feedback && (
          <Alert variant="warning" className="py-2 small">
            This job has already been accepted and may be mid-scoring. Removing someone releases only their{' '}
            <strong>unfinished</strong> candidates back to the shared pool for a teammate to pick up - anything they
            already completed keeps their name on it. Adding someone gives them immediate access. Changing the team
            leader only hands over report/finalize rights - no scores are affected.
          </Alert>
        )}
        {loading ? (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" /> Loading available examiners...
          </div>
        ) : (
          <>
            <Form.Label>Current selection</Form.Label>
            {selected.length === 0 && <p className="text-muted small">No examiners selected.</p>}
            <ul className="list-group mb-3">
              {selected.map((e) => (
                <li key={e.id} className="list-group-item d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-2">
                    {selected.length > 1 && (
                      <Form.Check
                        type="radio"
                        name="teamLeader"
                        checked={String(teamLeaderId) === String(e.id)}
                        onChange={() => setTeamLeaderId(String(e.id))}
                        label=""
                      />
                    )}
                    <span>
                      {e.name}
                      {selected.length > 1 && String(teamLeaderId) === String(e.id) && (
                        <span className="ms-1" style={{ color: '#0d6efd' }}>(team leader)</span>
                      )}
                    </span>
                  </div>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    disabled={selected.length <= 1}
                    onClick={() => handleRemove(e.id)}
                  >
                    Remove
                  </Button>
                </li>
              ))}
            </ul>

            <Form.Group className="mb-2">
              <Form.Label>Add examiner</Form.Label>
              <div className="d-flex gap-2">
                <Form.Select value={examinerToAdd} onChange={(e) => setExaminerToAdd(e.target.value)}>
                  <option value="">-- Select an Examiner --</option>
                  {selectableExaminers.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.name}
                    </option>
                  ))}
                </Form.Select>
                <Button variant="outline-primary" onClick={handleAddExaminer} disabled={!examinerToAdd}>
                  Add
                </Button>
              </div>
            </Form.Group>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={submitting || loading}>
          {submitting ? 'Saving...' : 'Save Team'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default EditTeamModal;
